import { useState } from "react";
import { ProductFeature } from "../../../api/featureRequests";
import { useDeployFeatureMutation } from "../hooks/useFeatureRequestsQueries";
import { errorMessage } from "../utils";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSAlert from "../../system-design/DSAlert";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";

const DeployFeatureDialog = (props: {
  open: boolean;
  feature: ProductFeature;
  onClose: () => void;
}) => {
  const deploy = useDeployFeatureMutation();
  const [version, setVersion] = useState("");

  const onEnter = () => {
    setVersion(props.feature.deployedVersion ?? "");
    deploy.reset();
  };

  const done = deploy.data;

  return (
    <DSDialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="xs"
      TransitionProps={{ onEnter }}
    >
      <DSDialogTitle>
        {props.feature.deployedVersion ? "Change version" : "Mark as deployed"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {done ? (
            <DSAlert severity="success">
              &quot;{props.feature.title}&quot; marked as deployed in{" "}
              {done.feature.deployedVersion}.{" "}
              {done.flaggedCount > 0
                ? `${done.flaggedCount} open request(s) are now "Potentially resolved" — review them in the Requests tab.`
                : "No open requests needed flagging."}
            </DSAlert>
          ) : (
            <>
              <DSTypography>
                All open requests for &quot;{props.feature.title}&quot; will be
                marked <strong>Potentially resolved</strong> so you can review
                them and decide whether to notify the customers. No emails are
                sent.
              </DSTypography>
              <DSTextField
                label="Version"
                placeholder="e.g. 643.2"
                required
                autoFocus
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              {deploy.isError && (
                <DSAlert severity="error">
                  {errorMessage(deploy.error, "Failed to mark as deployed.")}
                </DSAlert>
              )}
            </>
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={props.onClose}>
          {done ? "Close" : "Cancel"}
        </DSSecondaryButton>
        {!done && (
          <DSPrimaryButton
            disabled={!version.trim() || deploy.isPending}
            onClick={() =>
              deploy.mutate({ id: props.feature.id, version: version.trim() })
            }
          >
            Save
          </DSPrimaryButton>
        )}
      </DSDialogActions>
    </DSDialog>
  );
};

export default DeployFeatureDialog;
